import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Container, Row, Col, Card, Button, ButtonGroup } from 'react-bootstrap';
import { fetchLojaFisicaProdutos, checkPendingTasks } from '../../services/api';
import ProductTable from '../../components/ProductTable/ProductTable';
import ProductCard from '../../components/ProductCard/ProductCard';
import ProductImage from '../../components/ProductImage/ProductImage';

import Filtros from '../../components/Filtros/Filtros';
import Pagination from '../../components/Pagination/Pagination';
import Loading from '../../components/Loading/Loading';
import EmptyState from '../../components/EmptyState/EmptyState';

import RecomendacoesInteligentes from '../../components/RecomendacoesInteligentes/RecomendacoesInteligentes';
import TaskModal from '../../components/TaskModal/TaskModal';
import styles from './LojaFisicaCatalog.module.css';
import productTableStyles from '../../components/ProductTable/ProductTable.module.css';
import ViewToggle from '../../components/ViewToggle/ViewToggle';
import { saveCatalogState, getCatalogState, clearCatalogState } from '../../hooks/useCatalogState';
import { useCarrinhoComprasLojaFisica } from '../../contexts/CarrinhoComprasLojaFisicaContext';
import ListaComprasLojaFisicaPanel from '../../components/ListaComprasLojaFisicaPanel/ListaComprasLojaFisicaPanel';
import ModalAdicionarItemLojaFisica from '../../components/ModalAdicionarItemLojaFisica/ModalAdicionarItemLojaFisica';

const LojaFisicaCatalog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isInitialized = useRef(false);
  // Guard para processar o alerta apenas uma vez no mount
  const alertaJaProcessado = useRef(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [categoria, setCategoria] = useState('');
  const [loja, setLoja] = useState(''); // '' = todas, '001' = centro, '004' = caxias
  const [informacao, setInformacao] = useState('');
  const [mediaDia, setMediaDia] = useState('');
  const [infoFilter, setInfoFilter] = useState('all'); // 'all', 'recorrente', 'nao_comprar'
  const [sortConfig, setSortConfig] = useState({ field: 'vendasMesAtual', direction: 'desc' });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [pendingTasksMap, setPendingTasksMap] = useState({});

  // Task Modal State
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [taskProduct, setTaskProduct] = useState(null);

  // Tooltip State
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [activeProduct, setActiveProduct] = useState(null);

  // Carrinho de compras loja física
  // Estado do alerta proativo (vindo da tela de IA Analytics)
  const [alertaBanner, setAlertaBanner] = useState(null); // { titulo, gravidade, dados }
  // Códigos dos produtos com alerta (para filtro visual)
  const [codigosAlerta, setCodigosAlerta] = useState(new Set());

  const { modoAtivo, toggleModo, totalItens, adicionarItem, estaNoCarrinho, getQuantidade } = useCarrinhoComprasLojaFisica();
  const [listaAberta, setListaAberta] = useState(false);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);

  const handleClicarProduto = useCallback((produto) => {
    if (!modoAtivo) return;
    setProdutoSelecionado(produto);
  }, [modoAtivo]);

  const handleFecharModalAdicionar = useCallback(() => {
    setProdutoSelecionado(null);
  }, []);

  // Detectar alerta proativo passado via navigate state (vindo de IAAnalytics)
  // ⚠️ IMPORTANTE: array vazio [] e ref guard — evita re-renders que travam o useQuery
  useEffect(() => {
    if (alertaJaProcessado.current) return;
    const state = location.state;
    if (state?.alertaAtivo && state?.alertaTipo === 'ruptura_estoque') {
      alertaJaProcessado.current = true;
      setAlertaBanner({
        titulo: state.alertaTitulo || 'Produtos com risco de ruptura',
        gravidade: state.alertaGravidade || 'aviso',
        dados: state.alertaDados || [],
      });
      if (Array.isArray(state.alertaDados) && state.alertaDados.length > 0) {
        const codigos = new Set(
          state.alertaDados.map(p => String(p.codigo_interno || p.codigo || '')).filter(Boolean)
        );
        setCodigosAlerta(codigos);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restaurar estado salvo ao montar o componente
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    const savedState = getCatalogState('loja-fisica');
    if (savedState) {
      console.log('Restaurando estado do catálogo loja-fisica:', savedState);
      if (savedState.page) setPage(savedState.page);
      if (savedState.search) setSearch(savedState.search);
      if (savedState.fornecedor) setFornecedor(savedState.fornecedor);
      if (savedState.categoria) setCategoria(savedState.categoria);
      if (savedState.loja) setLoja(savedState.loja);
      if (savedState.informacao) setInformacao(savedState.informacao);
      if (savedState.infoFilter) setInfoFilter(savedState.infoFilter);
      if (savedState.sortConfig) setSortConfig(savedState.sortConfig);
      if (savedState.viewMode) setViewMode(savedState.viewMode);

      // Restaurar posição do scroll após um pequeno delay para os dados carregarem
      if (savedState.scrollY) {
        setTimeout(() => {
          window.scrollTo({ top: savedState.scrollY, behavior: 'auto' });
        }, 500);
      }

      // Limpar estado após restaurar
      clearCatalogState();
    }
  }, []);

  // Salvar estado SEMPRE que qualquer filtro mudar (para estar pronto quando navegar)
  useEffect(() => {
    // Não salvar durante a inicialização
    if (!isInitialized.current) return;

    saveCatalogState('loja-fisica', {
      page,
      search,
      fornecedor,
      categoria,
      loja,
      informacao,
      infoFilter,
      sortConfig,
      viewMode
    });
  }, [page, search, fornecedor, categoria, loja, informacao, infoFilter, sortConfig, viewMode]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['loja-fisica-produtos', page, search, fornecedor, categoria, loja, informacao, sortConfig.field, sortConfig.direction, mediaDia],
    queryFn: () => fetchLojaFisicaProdutos({
      page,
      search,
      fornecedor,
      categoria,
      loja,
      informacao,
      sort: sortConfig.field,
      order: sortConfig.direction,
      mediaDia
    }),
    staleTime: 2 * 60 * 1000, // 2 minutos
    gcTime: 5 * 60 * 1000     // 5 minutos no cache
  });

  // Verificar tarefas pendentes quando os produtos mudarem
  useEffect(() => {
    const verifyTasks = async () => {
      if (data?.data && data.data.length > 0) {
        const codes = data.data.map(p => p.codigoInterno);
        const result = await checkPendingTasks(codes);
        if (result.success) {
          const map = {};
          result.data.forEach(code => {
            map[code] = true;
          });
          setPendingTasksMap(map);
        }
      } else {
        setPendingTasksMap({});
      }
    };

    verifyTasks();
  }, [data]);

  const handleSearch = useCallback((searchTerm) => {
    setSearch(searchTerm);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback(({ fornecedor: newFornecedor, categoria: newCategoria, loja: newLoja, informacao: newInformacao, ordenacao: newOrdenacao, mediaDia: newMediaDia }) => {
    setFornecedor(newFornecedor);
    setCategoria(newCategoria);
    setLoja(newLoja || '');
    setInformacao(newInformacao);
    if (newMediaDia !== undefined) setMediaDia(newMediaDia || '');

    // Aplicar ordenação se fornecida
    if (newOrdenacao) {
      switch (newOrdenacao) {
        case 'total_vendas':
          setSortConfig({ field: 'totalVendas', direction: 'desc' });
          break;
        case 'mais_vendidos_mes':
          setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
          break;
        case 'menor_estoque':
          setSortConfig({ field: 'estoque', direction: 'asc' });
          break;
        case 'maior_estoque':
          setSortConfig({ field: 'estoque', direction: 'desc' });
          break;
        case 'maior_lucro':
          setSortConfig({ field: 'lucro', direction: 'desc' });
          break;
        case 'maior_media_dia':
          setSortConfig({ field: 'mediaDiariaAjustada', direction: 'desc' });
          break;
        case 'menor_media_dia':
          setSortConfig({ field: 'mediaDiariaAjustada', direction: 'asc' });
          break;
        default:
          setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
      }
    } else {
      // Resetar para ordenação padrão
      setSortConfig({ field: 'vendasMesAtual', direction: 'desc' });
    }

    setPage(1);
  }, []);

  const handleFilterInfo = useCallback(() => {
    // Ciclo: all → recorrente → nao_comprar → all
    setInfoFilter(prevFilter => {
      if (prevFilter === 'all') return 'recorrente';
      if (prevFilter === 'recorrente') return 'nao_comprar';
      return 'all';
    });
    setPage(1);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = useCallback((field) => {
    setSortConfig(prevConfig => {
      // Se clicar no mesmo campo, inverte a direção
      if (prevConfig.field === field) {
        return {
          field,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }
      // Se clicar em novo campo, começa com ordem decrescente para campos de vendas/estoque
      const fieldsDescFirst = ['totalVendas', 'mediaMensal', 'estoque', 'estoqueMinimo', 'lucro', 'vendasMesAtual'];
      return {
        field,
        direction: fieldsDescFirst.includes(field) ? 'desc' : 'asc'
      };
    });
  }, []);

  const handleOpenTaskModal = useCallback((produto) => {
    setTaskProduct(produto);
    setTaskModalOpen(true);
  }, []);

  // Tooltip Handlers
  const handleMouseEnter = useCallback((event, produto) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const tooltipWidth = 340;
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const margin = 10;

    let left = rect.left - tooltipWidth - 15;
    if (left < margin) {
      left = rect.right + 15;
    }
    if (left + tooltipWidth > windowWidth - margin) {
      left = windowWidth - tooltipWidth - margin;
    }

    const maxTooltipHeight = Math.floor(windowHeight * 0.9);
    let top = margin;
    if (rect.top < windowHeight / 2) {
      top = Math.max(margin, rect.top);
    } else {
      top = Math.max(margin, windowHeight - maxTooltipHeight - margin);
    }

    setTooltipPosition({
      top,
      left,
      maxHeight: maxTooltipHeight
    });
    setActiveProduct(produto);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setActiveProduct(null);
  }, []);

  const getImageSrc = (produto) => {
    return produto.imagemBase64; // Loja física uses imagemBase64
  };

  // Filtrar produtos localmente (além da API)
  const filteredProdutos = data?.data ? data.data.filter(produto => {
    // Helper para verificar tags (suporta array ou string)
    const info = produto.informacao;
    const hasTag = (tag) => {
      if (Array.isArray(info)) return info.includes(tag);
      return info === tag;
    };
    const hasNoInfo = () => {
      if (Array.isArray(info)) return info.length === 0;
      return !info;
    };

    // Filtro por informação (dropdown)
    if (informacao) {
      if (informacao === 'sem_informacao') {
        if (!hasNoInfo()) return false;
      } else {
        if (!hasTag(informacao)) return false;
      }
    }

    // Filtro cíclico de informações (clique no cabeçalho)
    if (infoFilter === 'recorrente') {
      if (!hasTag('recorrente')) return false;
    } else if (infoFilter === 'nao_comprar') {
      if (!hasTag('nao_comprar')) return false;
    }

    return true;
  }) : [];

  // Ordenar produtos de acordo com o sortConfig selecionado
  let sortedProdutos = [...filteredProdutos].sort((a, b) => {
    const field = sortConfig.field;
    const direction = sortConfig.direction;

    let valueA, valueB;

    switch (field) {
      case 'estoque':
        valueA = a.estoque || 0;
        valueB = b.estoque || 0;
        break;
      case 'totalVendas':
        valueA = a.totalVendas || 0;
        valueB = b.totalVendas || 0;
        break;
      case 'vendasPeriodo':
        valueA = a.vendasPeriodo || 0;
        valueB = b.vendasPeriodo || 0;
        break;
      case 'lucro':
        valueA = a.lucro || 0;
        valueB = b.lucro || 0;
        break;
      case 'markup':
        valueA = a.markup || 0;
        valueB = b.markup || 0;
        break;
      case 'mediaDiariaAjustada':
        valueA = parseFloat(a.mediaDiariaAjustada) || 0;
        valueB = parseFloat(b.mediaDiariaAjustada) || 0;
        break;
      case 'vendasMesAtual':
      default:
        valueA = a.vendasMesAtual || 0;
        valueB = b.vendasMesAtual || 0;
        break;
    }

    if (direction === 'asc') {
      return valueA - valueB;
    } else {
      return valueB - valueA;
    }
  });

  // Quando há alerta ativo de ruptura, elevar os produtos afetados ao topo da lista
  if (codigosAlerta.size > 0) {
    sortedProdutos = [
      ...sortedProdutos.filter(p => codigosAlerta.has(String(p.codigoInterno || ''))),
      ...sortedProdutos.filter(p => !codigosAlerta.has(String(p.codigoInterno || ''))),
    ];
  }


  return (
    <Container fluid className="p-4">
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <div>
            <h1 className={styles.pageTitle}>Catálogo Loja Física</h1>
            <p className={styles.pageSubtitle}>Gerencie o estoque das lojas físicas</p>
          </div>
        </div>

        <div className={styles.pageHeaderActions}>
          <ViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />

          {/* Botões de seleção de compras */}
          <button
            onClick={toggleModo}
            className={`${styles.pillBtn} ${modoAtivo ? styles.pillBtnGreen : ''}`}
          >
            🛒 {modoAtivo ? 'Modo Seleção ON' : 'Selecionar Compras'}
          </button>

          {totalItens > 0 && (
            <button
              onClick={() => setListaAberta(true)}
              className={`${styles.pillBtn} ${styles.pillBtnGreen}`}
            >
              📋 Lista ({totalItens})
            </button>
          )}

          {data?.pagination && (
            <div className="d-flex align-items-center gap-3">
              {/* Capital Investido no Estoque */}
              <div className={styles.capitalCard} title="Capital investido no estoque (preço de custo × quantidade)">
                <span className={styles.capitalIcon}>💰</span>
                <div>
                  <div className={styles.capitalLabel}>Capital Investido</div>
                  <div className={styles.capitalValue}>
                    {data.pagination.valorTotalEstoque?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) || 'R$ 0,00'}
                  </div>
                </div>
              </div>

              <div className={styles.statBlock}>
                <div className={styles.statBlockValue}>{data.pagination.total?.toLocaleString('pt-BR') || 0}</div>
                <div className={styles.statBlockLabel}>Produtos</div>
              </div>
              <div className={styles.statBlock}>
                <div className={styles.statBlockValue}>{sortedProdutos.length}</div>
                <div className={styles.statBlockLabel}>Exibindo</div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Banner de modo seleção ativo */}
      {modoAtivo && (
        <div className={styles.modeBanner}>
          <div className={styles.modeBannerText}>
            <span style={{ fontSize: '1.2rem' }}>🛒</span>
            <span>Modo de seleção ativo — clique em qualquer produto para adicioná-lo à lista</span>
          </div>
          <div className={styles.modeBannerActions}>
            {totalItens > 0 && (
              <button
                onClick={() => { toggleModo(); setListaAberta(true); }}
                className={styles.modeBannerBtn}
              >📋 Ver lista ({totalItens})</button>
            )}
            <button
              onClick={toggleModo}
              className={styles.modeBannerBtnGhost}
            >✕ Encerrar</button>
          </div>
        </div>
      )}

      {/* ── Banner de Alerta Proativo (vindo de IAAnalytics) ── */}
      {alertaBanner && (
        <div style={{
          background: alertaBanner.gravidade === 'critico' ? '#fdf2f0' : '#fef3c7',
          border: alertaBanner.gravidade === 'critico' ? '1px solid #f6d5d0' : '1px solid #fbbf24',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
        }}>
          <div style={{ color: alertaBanner.gravidade === 'critico' ? '#c0392b' : '#92400e', fontWeight: 600 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{ fontSize: '1.1rem' }}>⚠️</span>
              <span style={{ fontSize: '0.95rem' }}>{alertaBanner.titulo}</span>
              <span style={{
                background: alertaBanner.gravidade === 'critico' ? '#f6d5d0' : '#fde8a6',
                color: alertaBanner.gravidade === 'critico' ? '#c0392b' : '#92400e',
                borderRadius: '4px',
                padding: '1px 8px',
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}>{alertaBanner.gravidade?.toUpperCase()}</span>
            </div>
            {alertaBanner.dados?.length > 0 && (
              <div style={{ fontSize: '0.82rem', opacity: 0.85, fontWeight: 400, maxWidth: '640px', lineHeight: 1.5 }}>
                Produtos em risco destacados abaixo:
                {' '}<strong>{alertaBanner.dados.slice(0, 5).map(p => p.descricao?.substring(0, 30) || p.codigo_interno).join(', ')}</strong>
                {alertaBanner.dados.length > 5 && ` e mais ${alertaBanner.dados.length - 5}`}
              </div>
            )}
          </div>
          <button
            onClick={() => { setAlertaBanner(null); setCodigosAlerta(new Set()); }}
            style={{
              background: '#fff',
              border: alertaBanner.gravidade === 'critico' ? '1px solid #f6d5d0' : '1px solid #fbbf24',
              color: alertaBanner.gravidade === 'critico' ? '#c0392b' : '#92400e',
              borderRadius: '8px',
              padding: '5px 12px',
              cursor: 'pointer',
              fontSize: '0.82rem',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>✕ Fechar</button>
        </div>
      )}

      <RecomendacoesInteligentes />

      <Filtros
        onFilterChange={handleFilterChange}
        onSearch={handleSearch}
        initialFornecedor={fornecedor}
        initialCategoria={categoria}
        initialLoja={loja}
        initialInformacao={informacao}
        initialSearch={search}
        initialMediaDia={mediaDia}
      />

      {/* Início de mês: vendas importadas com ~2 dias de delay — backend cai para os mais vendidos do mês anterior */}
      {!isLoading && !isError && data?.ordenacaoMesAnterior && (
        <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: '0.85rem' }}>
          📅 As vendas deste mês ainda não foram importadas (delay de ~2 dias) — exibindo os{' '}
          <strong>mais vendidos de {data.ordenacaoMesAnterior.mes}/{data.ordenacaoMesAnterior.ano}</strong>.
        </div>
      )}

      {isLoading && <Loading />}

      {!isLoading && isError && (
        <div className="alert alert-danger">
          Erro ao carregar produtos: {error?.message || 'Erro desconhecido'}
        </div>
      )}

      {!isLoading && !isError && sortedProdutos.length === 0 && (
        <EmptyState message="Nenhum produto encontrado" />
      )}

      {!isLoading && !isError && sortedProdutos.length > 0 && (
        <Card className={styles.panel}>
          <Card.Header className={styles.panelHeader}>
            <h6 className={styles.panelHeaderTitle}>Lista de Produtos</h6>
          </Card.Header>
          <Card.Body className={viewMode === 'table' ? styles.panelBodyTable : styles.panelBodyGrid}>
            {viewMode === 'table' ? (
              <div className="table-responsive">
                <ProductTable
                  produtos={sortedProdutos}
                  tipo="loja-fisica"
                  onSort={handleSort}
                  sortConfig={sortConfig}
                  onFilterInfo={handleFilterInfo}
                  infoFilter={infoFilter}
                  onOpenTaskModal={handleOpenTaskModal}
                  modoListaCompras={modoAtivo}
                  onClicarProduto={handleClicarProduto}
                  estaNoCarrinho={estaNoCarrinho}
                  getQuantidade={getQuantidade}
                  codigosAlerta={codigosAlerta}
                />
              </div>
            ) : (
              <div className={styles.productGrid}>
                {sortedProdutos.map(produto => {
                  const temAlertaRuptura = codigosAlerta.size > 0 && codigosAlerta.has(String(produto.codigoInterno || ''));
                  return (
                    <div
                      key={produto.id || produto.codigoInterno}
                      onClick={() => handleClicarProduto(produto)}
                      className={temAlertaRuptura ? styles.cardAlertaRuptura : undefined}
                      style={modoAtivo ? { cursor: 'pointer' } : {}}
                    >
                      <ProductCard
                        produto={produto}
                        tipo="loja-fisica"
                        onOpenTaskModal={modoAtivo ? undefined : handleOpenTaskModal}
                        hasPendingTasks={pendingTasksMap[produto.codigoInterno]}
                        onMouseEnter={(e) => handleMouseEnter(e, produto)}
                        onMouseLeave={handleMouseLeave}
                        modoListaCompras={modoAtivo}
                        noCarrinho={estaNoCarrinho(produto.codigo || produto.codigoInterno)}
                        qtdNoCarrinho={getQuantidade(produto.codigo || produto.codigoInterno)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
          <Card.Footer className={styles.panelFooter}>
            <Pagination
              currentPage={page}
              totalPages={data.pagination?.totalPages || 1}
              onPageChange={handlePageChange}
            />
          </Card.Footer>
        </Card>
      )}

      <TaskModal
        show={taskModalOpen}
        onHide={() => setTaskModalOpen(false)}
        product={taskProduct}
        isEcommerce={false}
      />

      {/* Unified Tooltip for Grid View */}
      {viewMode === 'grid' && activeProduct && (
        <div
          className={productTableStyles.tooltip}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            maxHeight: tooltipPosition.maxHeight ? `${tooltipPosition.maxHeight}px` : '85vh',
            display: 'block',
            zIndex: 9999 // Ensure it's on top
          }}
        >
          <div className={productTableStyles.tooltipImageContainer}>
            <ProductImage
              src={getImageSrc(activeProduct)}
              alt={activeProduct.descricao}
              className={productTableStyles.tooltipMainImage}
            />
          </div>

          <div className={productTableStyles.tooltipHeader}>
            {activeProduct.descricao}
          </div>

          <div className={productTableStyles.tooltipContent}>
            {/* Seção de Reposições - derivada do histórico mensal (entradas consolidadas), máx 2 */}
            {(() => {
              const MAX_VISIBLE = 2;
              const fmtData = (s) => {
                if (!s) return '';
                const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
                return m ? `${m[3]}/${m[2]}/${m[1]}` : new Date(s).toLocaleDateString('pt-BR');
              };
              const reposicoes = (activeProduct.historicoEntradas || [])
                .map(e => ({ data: e.data, quantidade: e.quantidade }));

              if (reposicoes.length > 0) {
                const visiveis = reposicoes.slice(0, MAX_VISIBLE);
                const extras = reposicoes.length - MAX_VISIBLE;
                return (
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{
                      fontSize: '10px',
                      fontWeight: 700,
                      color: '#1e8449',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '4px'
                    }}>
                      📦 Reposicoes
                    </div>
                    {visiveis.map((entrada, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '3px 8px',
                        marginBottom: '2px',
                        background: i % 2 === 0 ? 'rgba(46, 204, 113, 0.08)' : 'transparent',
                        borderRadius: '4px',
                        fontSize: '10.5px'
                      }}>
                        <span style={{ color: '#555', minWidth: '72px', fontVariantNumeric: 'tabular-nums' }}>
                          {fmtData(entrada.data)}
                        </span>
                        <span style={{
                          background: '#27ae60',
                          color: '#fff',
                          padding: '1px 6px',
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: '10px'
                        }}>
                          +{entrada.quantidade} un
                        </span>
                      </div>
                    ))}
                    {extras > 0 && (
                      <div style={{ fontSize: '10px', color: '#7f8c8d', paddingLeft: '8px', marginTop: '2px' }}>
                        +{extras} anteriores
                      </div>
                    )}
                  </div>
                );
              }
              // Fallback: linha única de última entrada (comportamento anterior)
              if (activeProduct.ultimaEntrada) {
                return (
                  <div style={{
                    marginBottom: '8px',
                    padding: '6px 10px',
                    background: 'rgba(46, 204, 113, 0.15)',
                    borderRadius: '6px',
                    border: '1px solid rgba(46, 204, 113, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '11px'
                  }}>
                    <span style={{ color: '#2ecc71', fontWeight: 600 }}>📦 Entrada:</span>
                    <span style={{ color: '#1e8449', fontWeight: 700 }}>
                      {new Date(activeProduct.ultimaEntrada).toLocaleDateString('pt-BR')}
                    </span>
                    {activeProduct.qtdChegada12m > 0 && (
                      <span style={{ color: '#27ae60', fontSize: '10px' }}>
                        (+{activeProduct.qtdChegada12m} un.)
                      </span>
                    )}
                  </div>
                );
              }
              return null;
            })()}
            {activeProduct.vendasMensais && activeProduct.vendasMensais.length > 0 ? (
              <>
                <div className={productTableStyles.tooltipSubHeader}>
                  Histórico de Vendas
                </div>
                {activeProduct.vendasMensais.map((venda, index) => {
                  const nextVenda = activeProduct.vendasMensais[index + 1];
                  let percentChange = null;

                  if (nextVenda) {
                    if (nextVenda.quantidade > 0) {
                      percentChange = ((venda.quantidade - nextVenda.quantidade) / nextVenda.quantidade) * 100;
                    } else if (venda.quantidade > 0) {
                      percentChange = 100;
                    } else {
                      percentChange = 0;
                    }
                  }

                  return (
                    <div key={index} className={`${productTableStyles.tooltipRow} ${index % 2 !== 0 ? productTableStyles.rowStriped : ''}`}>
                      <span className={productTableStyles.tooltipLabel}>{venda.mes}</span>
                      <div className={productTableStyles.tooltipRightSide}>
                        {percentChange !== null && Math.abs(percentChange) > 0 && (
                          <span className={`${productTableStyles.percentChange} ${percentChange >= 0 ? productTableStyles.percentPositive : productTableStyles.percentNegative}`}>
                            {percentChange > 0 ? '▲' : '▼'} {Math.abs(percentChange).toFixed(0)}%
                          </span>
                        )}
                        <span className={productTableStyles.tooltipValue}>{venda.quantidade}</span>
                      </div>
                    </div>
                  );
                })}
                <div className={productTableStyles.tooltipDividerStrong}></div>
                <div className={productTableStyles.tooltipRow}>
                  <span className={productTableStyles.tooltipLabel}>Média Mensal</span>
                  <span className={productTableStyles.tooltipValue}>{activeProduct.mediaMensal || 0}</span>
                </div>
                {activeProduct.estoque > 0 && (
                  <div className={productTableStyles.tooltipRow}>
                    <span className={productTableStyles.tooltipLabel}>Média Diária</span>
                    <span className={productTableStyles.tooltipValue}>
                      {/* Média diária = total 90d ÷ dias-com-venda (backend) */}
                      {activeProduct.mediaDiariaAjustada > 0
                        ? activeProduct.mediaDiariaAjustada.toFixed(1)
                        : '—'}
                    </span>
                  </div>
                )}
                <div className={productTableStyles.tooltipDivider}></div>
                <div className={productTableStyles.tooltipInfo}>
                  <strong>Est. Mín: {activeProduct.estoqueMinimo}</strong><br />
                  <small>(Média Mensal × 1,5)</small>
                </div>
                {activeProduct.estoque > 0 && (
                  <div className={productTableStyles.tooltipInfoSecondary}>
                    <small>
                      Duração estimada: <strong>
                        {activeProduct.mediaMensal > 0
                          ? Math.floor(activeProduct.estoque / (activeProduct.mediaMensal / 30))
                          : '∞'
                        } dias
                      </strong>
                    </small>
                  </div>
                )}
              </>
            ) : (
              <div className={productTableStyles.tooltipInfo}>
                Sem dados de vendas disponíveis
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de adicionar produto à lista */}
      {produtoSelecionado && (
        <ModalAdicionarItemLojaFisica
          produto={produtoSelecionado}
          onClose={handleFecharModalAdicionar}
        />
      )}

      {/* Painel lateral da lista de compras - Loja Física */}
      {listaAberta && (
        <>
          <div
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1040 }}
            onClick={() => setListaAberta(false)}
          />
          <ListaComprasLojaFisicaPanel onClose={() => setListaAberta(false)} />
        </>
      )}
    </Container>
  );
};

export default LojaFisicaCatalog;
